import { useState } from 'react';
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  ArrowBack,
  Person,
  Groups,
  AttachMoney,
  HourglassEmpty,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { getErrorMessage } from '@billda/shared-ui';
import { useAllocationDetails } from '../../../hooks/useAllocationDetails';
import { useAdminCancelAllocation } from '../../../hooks/useAdminCancelAllocation';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { formatCurrency } from '../../../../../shared/utils/currency';

interface AdminCancelAllocationPanelProps {
  allocationId: string;
  /** Optional success callback - if provided, called instead of using useSitePanelState */
  onSuccess?: () => void;
  /** Optional back callback - shows a back button when provided */
  onBack?: () => void;
}

export function AdminCancelAllocationPanel({
  allocationId,
  onSuccess,
  onBack,
}: AdminCancelAllocationPanelProps) {
  // Only use site panel state if no onSuccess callback provided
  const sitePanelState = useSitePanelState();
  const handleSuccess = onSuccess ?? sitePanelState.closePanel;

  const { data: allocation, error: loadError } =
    useAllocationDetails(allocationId);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutate: adminCancel, isPending } = useAdminCancelAllocation();

  const handleConfirm = () => {
    setError(null);
    adminCancel(
      {
        allocationId,
        cancellationNotes: notes || undefined,
      },
      {
        onSuccess: () => {
          handleSuccess();
        },
        onError: err => {
          setError(getErrorMessage(err));
        },
      }
    );
  };

  // Loading state
  if (!allocation && !loadError) {
    return (
      <Box data-testid="skeleton-loader" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={100} />
        </Stack>
      </Box>
    );
  }

  // Error loading allocation
  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {loadError instanceof Error
            ? loadError.message
            : 'Failed to load allocation details'}
        </Alert>
      </Box>
    );
  }

  // No data
  if (!allocation) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Allocation not found</Alert>
      </Box>
    );
  }

  const getStatusConfig = () => {
    switch (allocation.status) {
      case 'InProgress':
        return {
          label: 'In Progress',
          color: 'info' as const,
          icon: <HourglassEmpty fontSize="small" />,
        };
      case 'Booked':
        return {
          label: 'Pending',
          color: 'warning' as const,
          icon: <HourglassEmpty fontSize="small" />,
        };
      case 'Approved':
        return {
          label: 'Approved',
          color: 'success' as const,
          icon: <CheckCircle fontSize="small" />,
        };
      case 'Rejected':
        return {
          label: 'Rejected',
          color: 'error' as const,
          icon: <Cancel fontSize="small" />,
        };
      default:
        return {
          label: allocation.status,
          color: 'default' as const,
          icon: <HourglassEmpty fontSize="small" />,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const isGang = allocation.worker.type === 'Gang';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {onBack && (
            <Button
              startIcon={<ArrowBack />}
              onClick={onBack}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              data-testid="back-to-allocation-details"
            >
              Back to Allocation Details
            </Button>
          )}

          {/* Allocation Summary Card */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                {/* Title and Status */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant="h6" component="h2">
                    Allocation Details
                  </Typography>
                  <Chip
                    label={statusConfig.label}
                    color={statusConfig.color}
                    icon={statusConfig.icon}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                {/* Worker Info */}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Worker
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isGang ? (
                      <Groups fontSize="small" color="action" />
                    ) : (
                      <Person fontSize="small" color="action" />
                    )}
                    <Typography variant="body1" fontWeight={600}>
                      {allocation.worker.displayName}
                    </Typography>
                  </Stack>
                </Box>

                {/* Amount */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney fontSize="small" color="action" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(allocation.amount)}
                    </Typography>
                  </Box>
                </Box>

                {/* Related Item */}
                <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    {allocation.relatedItem.itemType === 'LabourItem'
                      ? 'Labour Item'
                      : 'Variation Request'}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {allocation.relatedItem.itemName}
                  </Typography>
                </Box>

                {/* Location */}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2">
                    {allocation.plotContext.siteName} -{' '}
                    {allocation.plotContext.plotName}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Notes TextField */}
          <TextField
            label="Cancellation Notes (optional)"
            multiline
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add any notes about this cancellation..."
            fullWidth
            data-testid="cancellation-notes-input"
          />

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </Box>

      {/* Fixed bottom action area */}
      <Box
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Warning Alert */}
        <Alert severity="warning" sx={{ mb: 2 }}>
          {allocation.relatedItem.itemType === 'LabourItem' ? (
            <>
              Cancelling this allocation will return{' '}
              <strong>{formatCurrency(allocation.amount)}</strong> to the labour
              item&apos;s available pool. This action cannot be undone.
            </>
          ) : (
            <>
              Cancelling this variation allocation (
              <strong>{formatCurrency(allocation.amount)}</strong>) cannot be
              undone.
            </>
          )}
        </Alert>

        <Button
          variant="contained"
          color="error"
          size="large"
          fullWidth
          startIcon={
            isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CancelIcon />
            )
          }
          onClick={handleConfirm}
          disabled={isPending}
          data-testid="confirm-cancel-button"
        >
          {isPending ? 'Cancelling...' : 'Confirm Cancellation'}
        </Button>
      </Box>
    </Box>
  );
}
